import { useState, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLabels, useLabel, useReleases, useLabelSuggestions } from '../hooks'
import { LabelSuggestion } from '../api/labels'
import { LabelFilters, LabelSortField, Label } from '../types/label'
import { LabelHeader } from '../components/labels/LabelHeader'
import { ReleaseFilters } from '../types/release'
import { useViewSort, mapSortForEntity } from '../context/ViewSortContext'
import { LoadingState } from '../components/common/Spinner'
import { Pagination } from '../components/common/Pagination'
import { ViewSortControls } from '../components/common/ViewSortControls'
import { IconButton, SearchInput, Button, Badge } from '../components/common'
import { LabelGrid } from '../components/labels/LabelGrid'
import { LabelFilters as LabelFiltersComponent } from '../components/labels/LabelFilters'
import { ReleaseListView } from '../components/releases/ReleaseListView'
import { cn } from '../utils'
import styles from './PageStyles.module.css'
import labelStyles from '../components/labels/Labels.module.css'
import releaseStyles from '../components/releases/Releases.module.css'

interface LabelTab {
  id: number
  name: string
  labelName: string
}

export function LabelsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const { viewMode, sort: globalSort, order: globalOrder } = useViewSort()

  const [filters, setFilters] = useState<LabelFilters>(() => ({
    page: 1,
    per_page: 50,
    sort: mapSortForEntity(globalSort) as LabelSortField,
    order: globalOrder,
  }))
  const [search, setSearch] = useState('')
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [openTabs, setOpenTabs] = useState<LabelTab[]>(() => {
    try {
      const saved = localStorage.getItem('stonks_label_tabs')
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })
  const [activeTabId, setActiveTabId] = useState<number | null>(() => {
    try {
      const saved = localStorage.getItem('stonks_label_active_tab')
      return saved ? parseInt(saved) : null
    } catch {
      return null
    }
  })
  const [showFilters, setShowFilters] = useState(false)
  const [releasePage, setReleasePage] = useState(1)
  const [releaseSearch, setReleaseSearch] = useState('')

  // Open label tab from URL params (e.g. from player link)
  useEffect(() => {
    const labelName = searchParams.get('label')
    if (!labelName) return
    setOpenTabs(prev => {
      const existing = prev.find(t => t.labelName === labelName)
      if (existing) {
        setActiveTabId(existing.id)
        return prev
      }
      const newTab = { id: Date.now(), name: labelName, labelName }
      setActiveTabId(newTab.id)
      return [...prev, newTab]
    })
    setSearchParams({}, { replace: true })
  }, [searchParams, setSearchParams])

  // Sync global sort changes to label list filters
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      sort: mapSortForEntity(globalSort) as LabelSortField,
      order: globalOrder,
      page: 1,
    }))
  }, [globalSort, globalOrder])

  const { data, isLoading, error } = useLabels(filters)
  const { data: suggestions = [] } = useLabelSuggestions(search)

  // Get releases for active tab using global sort
  const activeTab = openTabs.find(t => t.id === activeTabId)
  const { data: labelDetail } = useLabel(activeTab?.labelName ?? '')
  const releaseFilters: ReleaseFilters = {
    label: activeTab?.labelName,
    page: releasePage,
    per_page: 50,
    sort: globalSort,
    order: globalOrder,
    ...(releaseSearch ? { q: releaseSearch } : {}),
  }

  // Save tabs to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('stonks_label_tabs', JSON.stringify(openTabs))
    } catch { /* ignore */ }
  }, [openTabs])

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    try {
      if (activeTabId !== null) {
        localStorage.setItem('stonks_label_active_tab', activeTabId.toString())
      } else {
        localStorage.removeItem('stonks_label_active_tab')
      }
    } catch { /* ignore */ }
  }, [activeTabId])

  const { data: releasesData, isLoading: isLoadingReleases } = useReleases(
    releaseFilters,
    { enabled: activeTabId !== null && !!activeTab?.labelName }
  )

  // Accept the value from SearchField's onSubmit so clearing (which fires
  // onSubmit('')) doesn't re-apply the stale `search` closure — that left the
  // query stuck and re-persisted it, so it kept coming back.
  const handleSearch = useCallback((value?: string) => {
    setShowSuggestions(false)
    const q = value !== undefined ? value : search
    setFilters(prev => ({ ...prev, q: q || undefined, page: 1 }))
  }, [search])

  const handleClearSearch = () => {
    setSearch('')
    setShowSuggestions(false)
    setFilters(prev => ({ ...prev, q: undefined, page: 1 }))
  }

  const handleSuggestionClick = (suggestion: LabelSuggestion) => {
    setShowSuggestions(false)
    setSearch(suggestion.name)
    handleLabelClick({ name: suggestion.name } as Label)
  }

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleLabelClick = (label: Label) => {
    const existingTab = openTabs.find(t => t.labelName === label.name)
    if (existingTab) {
      setActiveTabId(existingTab.id)
    } else {
      const newTab: LabelTab = {
        id: Date.now(),
        name: label.name,
        labelName: label.name,
      }
      setOpenTabs(prev => [...prev, newTab])
      setActiveTabId(newTab.id)
    }
  }

  const handleCloseTab = (e: React.MouseEvent, tabId: number) => {
    e.stopPropagation()
    setOpenTabs(prev => prev.filter(t => t.id !== tabId))
    if (activeTabId === tabId) {
      setActiveTabId(null)
      setReleasePage(1)
    }
  }

  const handleTabClick = (tabId: number | null) => {
    setActiveTabId(tabId)
    setReleasePage(1)
    setReleaseSearch('')
  }

  if (error) {
    return (
      <div className={styles.error}>
        <i className="bi bi-exclamation-triangle"></i>
        <p>Failed to load labels</p>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <h1>
            Labels
          </h1>
        </div>
      </div>

      {/* Tab bar — always visible (even with no open tabs) for stable layout */}
      <div className={releaseStyles.tabBar}>
        <button
          className={cn(releaseStyles.tab, activeTabId === null && releaseStyles.tabActive)}
          onClick={() => handleTabClick(null)}
        >
          All Labels
        </button>
        {openTabs.map(tab => (
          <button
            key={tab.id}
            className={cn(releaseStyles.tab, activeTabId === tab.id && releaseStyles.tabActive)}
            onClick={() => handleTabClick(tab.id)}
          >
            {tab.name}
            <span className={releaseStyles.tabClose} onClick={(e) => handleCloseTab(e, tab.id)}>
              <i className="bi bi-x"></i>
            </span>
          </button>
        ))}
      </div>

      {/* Search + sort toolbar — list view only */}
      {activeTabId === null && (
        <>
          <div className={releaseStyles.searchFilterRow}>
            <div className={labelStyles.searchBarWrap}>
              <SearchInput
                value={search}
                onChange={v => { setSearch(v); setShowSuggestions(true) }}
                onSubmit={handleSearch}
                onClear={handleClearSearch}
                onFocus={() => setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                onKeyDown={e => { if (e.key === 'Escape') setShowSuggestions(false) }}
                placeholder="Search labels..."
                block
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className={labelStyles.suggestions}>
                  {suggestions.map(s => (
                    <button
                      key={s.name}
                      className={labelStyles.suggestionItem}
                      onMouseDown={(e) => { e.preventDefault(); handleSuggestionClick(s) }}
                    >
                      <span className={labelStyles.suggestionName}>{s.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {(() => {
              const count = [
                filters.min_elevated_score, filters.min_match_score,
                filters.min_release_count, filters.min_want,
                filters.year_from, filters.year_to,
              ].filter(v => v != null).length
                + (filters.genres?.length ? 1 : 0)
                + (filters.styles?.length ? 1 : 0)
                + (filters.countries?.length ? 1 : 0)
              return (
                <Button
                  variant="secondary"
                  active={showFilters}
                  icon={showFilters ? 'bi bi-funnel-fill' : 'bi bi-funnel'}
                  onClick={() => setShowFilters(!showFilters)}
                >
                  Filters
                  {count > 0 && <Badge count>{count}</Badge>}
                </Button>
              )
            })()}
            {filters.genres?.map(g => (
              <span key={`g-${g}`} className={releaseStyles.filterChip}>
                {g}
                <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${g} filter`}
                  onClick={() => setFilters(prev => ({ ...prev, genres: prev.genres?.filter(x => x !== g), page: 1 }))} />
              </span>
            ))}
            {filters.styles?.map(s => (
              <span key={`s-${s}`} className={releaseStyles.filterChip}>
                {s}
                <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${s} filter`}
                  onClick={() => setFilters(prev => ({ ...prev, styles: prev.styles?.filter(x => x !== s), page: 1 }))} />
              </span>
            ))}
            {filters.countries?.map(c => (
              <span key={`c-${c}`} className={releaseStyles.filterChip}>
                {c}
                <IconButton variant="ghost" size="sm" icon="bi bi-x" aria-label={`Remove ${c} filter`}
                  onClick={() => setFilters(prev => ({ ...prev, countries: prev.countries?.filter(x => x !== c), page: 1 }))} />
              </span>
            ))}
            <div className={styles.controlsTrailing}>
              <ViewSortControls hideViewToggle />
            </div>
          </div>
          {showFilters && (
            <LabelFiltersComponent filters={filters} onFiltersChange={setFilters} onClose={() => setShowFilters(false)} />
          )}
        </>
      )}

      {/* Content */}
      {activeTabId === null ? (
        <div className={styles.gridView}>
          {isLoading ? (
            <LoadingState message="Loading labels..." />
          ) : (
            <>
              {data?.labels && data.labels.length > 0 ? (
                <LabelGrid labels={data.labels} onLabelClick={handleLabelClick} />
              ) : (
                <div className={styles.empty}>
                  <i className="bi bi-tag"></i>
                  <p>No labels found</p>
                  {filters.q && (
                    <p className={styles.emptyHint}>Try adjusting your search</p>
                  )}
                </div>
              )}

              {data?.pagination && (
                <Pagination
                  page={data.pagination.page}
                  totalPages={data.pagination.pages}
                  hasNext={data.pagination.page < data.pagination.pages}
                  hasPrev={data.pagination.page > 1}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      ) : (
        <div className={styles.scrollContent}>
          {labelDetail?.label && <LabelHeader label={labelDetail.label} />}

          {/* Search + sort toolbar — below label header */}
          <div className={releaseStyles.searchFilterRow}>
            <div className={releaseStyles.searchBarStandalone}>
              <i className="bi bi-search" />
              <input
                type="text"
                placeholder="Search releases..."
                value={releaseSearch}
                onChange={e => { setReleaseSearch(e.target.value); setReleasePage(1) }}
              />
              {releaseSearch && (
                <IconButton variant="ghost" size="sm" icon="bi bi-x-lg" aria-label="Clear search" onClick={() => { setReleaseSearch(''); setReleasePage(1) }} />
              )}
            </div>
            <div className={styles.controlsTrailing}>
              <ViewSortControls />
            </div>
          </div>

          {isLoadingReleases ? (
            <LoadingState message="Loading releases..." />
          ) : (
            <>
              <ReleaseListView
                releases={releasesData?.releases ?? []}
                viewMode={viewMode}
                emptyMessage="No releases found for this label"
              />
              {releasesData?.pagination && (
                <Pagination
                  page={releasePage}
                  totalPages={releasesData.pagination.total_pages}
                  hasNext={releasePage < releasesData.pagination.total_pages}
                  hasPrev={releasePage > 1}
                  onPageChange={(p) => setReleasePage(p)}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
